import { useMutation, useQuery } from "@tanstack/react-query"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { CheckCircle2, Mail, ShieldCheck } from "lucide-react"
import { toast } from "sonner"

import { Logo } from "@/components/logo"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { hasActiveSession } from "@/http/auth/session"
import { acceptInvite, getInvite } from "@/http/invites"
import { useOrganization } from "@/store/organization"

export const Route = createFileRoute("/invite/$inviteId")({
  component: InvitePage,
})

function InvitePage() {
  const { inviteId } = Route.useParams()
  const navigate = useNavigate()
  const isAuthenticated = hasActiveSession()
  const { loadOrganizations } = useOrganization()

  const {
    data: inviteResponse,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ["invite", inviteId],
    queryFn: async () => {
      const response = await getInvite(inviteId)
      if (response.error) {
        throw new Error(response.error.message)
      }
      return response.data
    },
  })

  const { mutate: accept, isPending } = useMutation({
    mutationFn: async () => {
      return acceptInvite(inviteId)
    },
    onSuccess: async (response) => {
      if (response.error) {
        toast.error(response.error.message || "Erro ao aceitar convite")
        return
      }

      await loadOrganizations()
      const { organizations } = useOrganization.getState()
      if (organizations.length === 1) {
        navigate({
          to: "/$orgSlug/dashboard",
          params: { orgSlug: organizations[0].slug },
        })
        return
      }

      if (organizations.length > 1) {
        navigate({ to: "/select-organization" })
        return
      }

      navigate({ to: "/home" })
    },
    onError: () => {
      toast.error("Erro ao aceitar convite")
    },
  })

  if (isLoading) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-6 p-6 md:p-10">
        <div className="flex w-full max-w-sm flex-col gap-6">
          <div className="flex items-center self-center">
            <Logo size="lg" />
          </div>
          <Card>
            <CardHeader className="text-center">
              <CardTitle>Carregando convite</CardTitle>
            </CardHeader>
            <CardContent className="flex justify-center py-6">
              <Mail className="size-10 text-muted-foreground animate-pulse" />
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  if (isError || !inviteResponse) {
    return (
      <div className="flex min-h-svh flex-col items-center justify-center gap-6 p-6 md:p-10">
        <div className="flex w-full max-w-sm flex-col gap-6">
          <div className="flex items-center self-center">
            <Logo size="lg" />
          </div>
          <Card>
            <CardHeader className="text-center">
              <CardTitle>Convite não encontrado</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4 text-center">
              <p className="text-sm text-muted-foreground">
                Este convite não existe ou expirou.
              </p>
              <Button asChild className="w-full">
                <Link to="/">Ir para login</Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    )
  }

  const invite = inviteResponse.invite
  const roleLabel = invite.role === "admin" ? "Administrador" : "Membro"
  const authorName = invite.author?.name || "Equipe"

  return (
    <div className="flex min-h-svh flex-col items-center justify-center gap-6 p-6 md:p-10">
      <div className="flex w-full max-w-md flex-col gap-6">
        <div className="flex items-center self-center">
          <Logo size="lg" />
        </div>
        <Card>
          <CardHeader className="text-center">
            <CardTitle>Convite para organização</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-1 text-center">
              <p className="text-lg font-medium">{invite.organization.name}</p>
              <p className="text-sm text-muted-foreground">
                Convidado por {authorName}
              </p>
              <p className="text-sm text-muted-foreground">
                Cargo: {roleLabel}
              </p>
            </div>

            {isAuthenticated ? (
              <Button
                className="w-full"
                onClick={() => accept()}
                disabled={isPending}
              >
                <CheckCircle2 className="mr-2 h-4 w-4" />
                Aceitar convite
              </Button>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
                  <ShieldCheck className="h-4 w-4" />
                  Faca login para aceitar o convite.
                </div>
                <Button asChild className="w-full">
                  <Link to="/">Ir para login</Link>
                </Button>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
